import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import OutsideClickDetector from '@components/Shared/OutsideClickDetector';
import Button from '@components/Button';
import { ReactComponent as DropdownArrow } from '@images/chevron-arrow.svg';

import { StyledDropdown } from './StyledDropdown';
import { Option } from './DropdownMenuItem';
import DropdownMenu from './DropdownMenu';

interface DropdownProps {
  color: string;
  isOpen: boolean;
  onToggle: (opened: boolean) => void;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
}

export interface DropdownHandle {
  getTotalHeight: () => number;
}

const Dropdown = forwardRef<DropdownHandle, DropdownProps>((props, ref) => {
  const {
    color,
    isOpen,
    onToggle,
    options,
    value,
    onChange,
  } = props;

  const menuRef = useRef<HTMLOListElement>(null);
  useImperativeHandle(ref, () => ({
    getTotalHeight: () => {
      if (!menuRef.current) return 0;
      const { offsetTop, offsetHeight } = menuRef.current;
      return offsetTop + offsetHeight;
    },
  }), []);

  const hasMultipleOptions = useMemo(() => options.length > 1, [options]);

  const selectedOption = useMemo(() => {
    const match = options.find(option => option.value === value);
    return match || options[0];
  }, [options, value]);

  const onSelect = useCallback((nextValue: string) => {
    onChange(nextValue);
    onToggle(false);
  }, [onChange, onToggle]);

  return (
    <StyledDropdown id='dropdown' color={color}>
      <Button
        type="button"
        className={`select${isOpen ? ' opened' : ''}`}
        disabled={!hasMultipleOptions}
        aria-label="default contract option"
        onClick={() => onToggle(!isOpen)}
      >
        {selectedOption.label}
        {hasMultipleOptions && <DropdownArrow className={`arrow${isOpen ? ' rotated' : ''}`} />}
      </Button>

      {isOpen && (
        <OutsideClickDetector callback={() => onToggle(false)}>
          <DropdownMenu
            ref={menuRef}
            options={options}
            selectedOption={selectedOption}
            onSelect={onSelect}
          />
        </OutsideClickDetector>
      )}
    </StyledDropdown>
  );
});

Dropdown.displayName = 'Dropdown';

export default Dropdown;
